import React, { FC } from "react";
import {
  MenuItem,
  Select,
  SelectProps,
  SelectVariants,
  SxProps,
  Theme,
  Tooltip,
} from "@mui/material";
import { isEmpty } from "lodash";
import { makeSxStyles } from "../helpers/make-sx-styles";

export interface ControlledSelectOption {
  label: string | React.ReactNode;
  value: string;
  disabled?: boolean;
  selectedLabel?: string;
  tooltip?: string;
}

export interface ControlledSelectProps
  extends Omit<SelectProps, "placeholder" | "onChange" | "allow"> {
  value?: string;
  placeholder: string;
  onChange?: (value: string) => void;
  options: ControlledSelectOption[];
  placeholderSelectEnabled?: boolean;
  placeholdeShow?: boolean;
  placeholderSelectLabel?: string;
  variant?: SelectVariants;
  maxHeight?: number;
  maxWidth?: number;
}

export const ControlledSelect: FC<ControlledSelectProps> = (props) => {
  const {
    value,
    placeholder,
    onChange,
    options,
    placeholderSelectEnabled = true,
    placeholderSelectLabel = "All",
    placeholdeShow = true,
    variant = "filled",
    maxHeight = "400px",
    sx,
    maxWidth,
    MenuProps: menuPropsFromProps,
    ...rest
  } = props;
  const styles = useStyles();

  return (
    <Select
      {...rest}
      displayEmpty
      disableUnderline
      size="small"
      variant={variant}
      value={isEmpty(value) ? "none" : value}
      onChange={(e) => onChange?.(e.target.value as string)}
      renderValue={(selected) => {
        if (selected === "none") {
          return placeholder;
        }
        const selectedOption = options.find((o) => o.value === selected);
        return selectedOption?.selectedLabel || selectedOption?.label;
      }}
      MenuProps={{
        ...menuPropsFromProps,
        PaperProps: {
          ...menuPropsFromProps?.PaperProps,
          sx: {
            ...styles.menuItem,
            ...menuPropsFromProps?.PaperProps?.sx,
          } as SxProps<Theme>,
          style: {
            maxHeight,
            maxWidth,
            ...menuPropsFromProps?.PaperProps?.style,
          },
        },
      }}
      sx={
        {
          ...(variant === "outlined" && styles.container),
          ...sx,
        } as SxProps<Theme>
      }
    >
      {!placeholderSelectEnabled && (
        <MenuItem
          value="none"
          disabled={!placeholderSelectEnabled}
          sx={placeholdeShow ? {} : { display: "none" }}
        >
          {placeholderSelectLabel}
        </MenuItem>
      )}
      {options.map((o) => {
        const menuItem = (
          <MenuItem
            disabled={o.disabled}
            key={o.value}
            value={o.value}
            data-testid="drowpdown-item"
            title={o.disabled ? undefined : o.tooltip}
            onClickCapture={
              o.disabled && o.tooltip
                ? (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }
                : undefined
            }
            sx={
              o.disabled && o.tooltip
                ? { "&.Mui-disabled": { pointerEvents: "auto" } }
                : undefined
            }
          >
            {o.disabled && o.tooltip ? (
              <Tooltip
                title={o.tooltip}
                enterDelay={150}
                enterNextDelay={150}
                placement="left"
                describeChild
                disableInteractive
              >
                <span style={{ display: "block", width: "100%" }}>
                  {o.label}
                </span>
              </Tooltip>
            ) : (
              o.label
            )}
          </MenuItem>
        );

        return menuItem;
      })}
    </Select>
  );
};

const useStyles = makeSxStyles(({ palette: { grey } }) => ({
  container: {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: grey[300],
    },
  },
  menuItem: {
    color: grey[100],
  },
}));
